import { Button, Input, Select, Space, Tag, message } from 'antd'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { assessBriefReadiness } from '@/lib/thesisFramework/briefReadiness'
import type { Construct, ThesisBrief } from '@/lib/thesisFramework/types'
import { useThesisBriefStore } from '@/stores/thesisBriefStore'

const METHOD_OPTS = [
  '案例研究',
  '问卷调研',
  '访谈研究',
  '二手数据分析',
  '混合方法',
].map((v) => ({ value: v, label: v }))

/** 侧栏：开题卡 SSOT —— 问题 / 框架 / 方法 / 边界（防抖自动保存） */
export default function ThesisBriefPanel() {
  const { novelId } = useParams()
  const ensureBrief = useThesisBriefStore((s) => s.ensureBrief)
  const updateBrief = useThesisBriefStore((s) => s.updateBrief)

  const [draft, setDraft] = useState<ThesisBrief | null>(null)
  const [saveHint, setSaveHint] = useState<'idle' | 'dirty' | 'saving' | 'saved'>('idle')
  const skipAutosave = useRef(true)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // 仅换文时从 store 灌入；之后靠本地 draft + 防抖写回，避免 saved 后被 stored 回写打断
  useEffect(() => {
    if (!novelId) return
    skipAutosave.current = true
    setDraft(ensureBrief(novelId))
    setSaveHint('idle')
  }, [novelId, ensureBrief])

  useEffect(() => {
    if (!novelId || !draft) return
    if (skipAutosave.current) {
      skipAutosave.current = false
      return
    }
    setSaveHint('dirty')
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      setSaveHint('saving')
      updateBrief(novelId, draft)
      setSaveHint('saved')
    }, 800)
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [draft, novelId, updateBrief])

  const readiness = useMemo(
    () => (draft ? assessBriefReadiness(draft) : null),
    [draft],
  )

  if (!novelId || !draft) {
    return <div className="p-4 thesis-muted text-sm">请打开一篇论文</div>
  }

  const patch = (p: Partial<ThesisBrief>) => setDraft({ ...draft, ...p })

  const saveNow = () => {
    if (timerRef.current) clearTimeout(timerRef.current)
    updateBrief(novelId, draft)
    setSaveHint('saved')
    message.success('开题卡已保存')
  }

  const setRq = (i: number, v: string) => {
    const researchQuestions = [...draft.researchQuestions]
    researchQuestions[i] = v
    patch({ researchQuestions })
  }

  const setConstruct = (i: number, p: Partial<Construct>) => {
    const keyConstructs = draft.keyConstructs.map((c, idx) =>
      idx === i ? { ...c, ...p } : c,
    )
    patch({ keyConstructs })
  }

  const hintText =
    saveHint === 'dirty'
      ? '有未保存修改…'
      : saveHint === 'saving'
        ? '保存中…'
        : saveHint === 'saved'
          ? '已自动保存'
          : '改完即存'

  return (
    <div className="thesis-brief-panel flex h-full flex-col overflow-hidden bg-[var(--tt-bg-mid,#ebe8e0)]">
      <div className="flex-none border-b border-[var(--tt-line,rgba(60,50,40,0.12))] px-3 py-2">
        <div className="flex items-center justify-between gap-2">
          <strong>开题卡</strong>
          <div className="flex items-center gap-2">
            <span className="thesis-muted text-xs">{hintText}</span>
            <Tag color={readiness?.ready ? 'success' : 'warning'}>
              {readiness?.score ?? 0}%
            </Tag>
          </div>
        </div>
        {readiness && !readiness.ready ? (
          <p className="thesis-muted mt-1 text-xs">
            未就绪：{readiness.gaps.map((g) => g.label).join('、')}
          </p>
        ) : null}
      </div>

      <div className="grow space-y-5 overflow-y-auto px-3 py-3 text-sm">
        <section>
          <h4 className="mb-2 font-semibold">一、实践与问题</h4>
          <label className="thesis-muted mb-1 block text-xs">实践背景</label>
          <Input.TextArea
            rows={3}
            value={draft.practiceBackground}
            onChange={(e) => patch({ practiceBackground: e.target.value })}
            placeholder="行业/企业/岗位情境…"
          />
          <label className="thesis-muted mb-1 mt-2 block text-xs">管理痛点</label>
          <Input.TextArea
            rows={2}
            value={draft.problemStatement}
            onChange={(e) => patch({ problemStatement: e.target.value })}
            placeholder="可观察的痛点，避免空泛…"
          />
          <label className="thesis-muted mb-1 mt-2 block text-xs">研究目的</label>
          <Input
            value={draft.researchPurpose}
            onChange={(e) => patch({ researchPurpose: e.target.value })}
            placeholder="一句话"
          />
          <label className="thesis-muted mb-1 mt-2 block text-xs">研究问题 RQ</label>
          <Space direction="vertical" className="w-full">
            {draft.researchQuestions.map((q, i) => (
              <Input.TextArea
                key={i}
                rows={2}
                value={q}
                onChange={(e) => setRq(i, e.target.value)}
                placeholder={`RQ${i + 1}`}
              />
            ))}
            <Button
              size="small"
              type="dashed"
              block
              onClick={() => patch({ researchQuestions: [...draft.researchQuestions, ''] })}
            >
              添加子问题
            </Button>
          </Space>
        </section>

        <section>
          <h4 className="mb-2 font-semibold">二、理论与框架</h4>
          <label className="thesis-muted mb-1 block text-xs">理论视角</label>
          <Input
            value={draft.theoreticalLens}
            onChange={(e) => patch({ theoreticalLens: e.target.value })}
            placeholder="如：动态能力 / RBV / 制度理论…"
          />
          <label className="thesis-muted mb-1 mt-2 block text-xs">关键构念</label>
          {draft.keyConstructs.map((c, i) => (
            <div
              key={i}
              className="mb-3 space-y-1 border-b border-[var(--tt-line,rgba(60,50,40,0.1))] pb-3 last:border-0"
            >
              <Input
                placeholder="构念名"
                value={c.name}
                onChange={(e) => setConstruct(i, { name: e.target.value })}
              />
              <Input.TextArea
                rows={2}
                placeholder="概念定义（文献口径）"
                value={c.definition}
                onChange={(e) => setConstruct(i, { definition: e.target.value })}
              />
              <Input.TextArea
                rows={2}
                placeholder="操作化 / 测量或编码方式"
                value={c.operationalization}
                onChange={(e) => setConstruct(i, { operationalization: e.target.value })}
              />
            </div>
          ))}
          <Button
            size="small"
            type="dashed"
            block
            onClick={() =>
              patch({
                keyConstructs: [
                  ...draft.keyConstructs,
                  { name: '', definition: '', operationalization: '' },
                ],
              })
            }
          >
            添加构念
          </Button>
          <label className="thesis-muted mb-1 mt-2 block text-xs">分析框架</label>
          <Input.TextArea
            rows={3}
            value={draft.analysisFramework}
            onChange={(e) => patch({ analysisFramework: e.target.value })}
            placeholder="构念如何串成可分析逻辑…"
          />
          <label className="thesis-muted mb-1 mt-2 block text-xs">假设/命题（可选）</label>
          <Input.TextArea
            rows={2}
            value={draft.hypothesesOrPropositions.join('\n')}
            onChange={(e) =>
              patch({
                hypothesesOrPropositions: e.target.value
                  .split('\n')
                  .map((s) => s.trim())
                  .filter(Boolean),
              })
            }
            placeholder="每行一条；探索性案例可留空"
          />
        </section>

        <section>
          <h4 className="mb-2 font-semibold">三、方法</h4>
          <label className="thesis-muted mb-1 block text-xs">方法类型</label>
          <Select
            className="w-full"
            options={METHOD_OPTS}
            value={draft.methodType}
            onChange={(v) => patch({ methodType: v })}
          />
          <label className="thesis-muted mb-1 mt-2 block text-xs">分析单位</label>
          <Input
            value={draft.unitOfAnalysis}
            onChange={(e) => patch({ unitOfAnalysis: e.target.value })}
            placeholder="门店 / 项目团队 / 企业…"
          />
          <label className="thesis-muted mb-1 mt-2 block text-xs">案例/样本</label>
          <Input.TextArea
            rows={2}
            value={draft.caseOrSample}
            onChange={(e) => patch({ caseOrSample: e.target.value })}
          />
          <label className="thesis-muted mb-1 mt-2 block text-xs">数据来源（每行一类）</label>
          <Input.TextArea
            rows={2}
            value={draft.dataSources.join('\n')}
            onChange={(e) =>
              patch({
                dataSources: e.target.value.split('\n').map((s) => s.trim()).filter(Boolean),
              })
            }
          />
          <label className="thesis-muted mb-1 mt-2 block text-xs">分析计划</label>
          <Input.TextArea
            rows={2}
            value={draft.analysisPlan}
            onChange={(e) => patch({ analysisPlan: e.target.value })}
          />
        </section>

        <section>
          <h4 className="mb-2 font-semibold">四、边界与贡献</h4>
          <label className="thesis-muted mb-1 block text-xs">研究范围内</label>
          <Input.TextArea
            rows={2}
            value={draft.scopeIn}
            onChange={(e) => patch({ scopeIn: e.target.value })}
          />
          <label className="thesis-muted mb-1 mt-2 block text-xs">明确不写（防跑偏）</label>
          <Input.TextArea
            rows={2}
            value={draft.scopeOut}
            onChange={(e) => patch({ scopeOut: e.target.value })}
            placeholder="例如：不做跨国比较；不评价具体厂商选型…"
          />
          <label className="thesis-muted mb-1 mt-2 block text-xs">预期贡献（每行一条）</label>
          <Input.TextArea
            rows={2}
            value={draft.expectedContributions.join('\n')}
            onChange={(e) =>
              patch({
                expectedContributions: e.target.value
                  .split('\n')
                  .map((s) => s.trim())
                  .filter(Boolean),
              })
            }
          />
        </section>
      </div>

      <div className="flex-none border-t border-[var(--tt-line,rgba(60,50,40,0.12))] px-3 py-2">
        <Button type="default" size="small" block onClick={saveNow}>
          立即保存
        </Button>
      </div>
    </div>
  )
}
